// Design System Color Tokens
// Centralized color definitions following the three-tier strategy

export const DESIGN_TOKENS = {
  // Updated "Natural Intelligence" Brand Colors - Blue-based Identity
  colors: {
    // Brand (nova paleta azul)
    brand: {
      primary: "#004E64", // Azul principal (antes "green")
      primaryVariant: "#003A4D", // Azul mais escuro
      primaryLight: "#006B85", // Azul claro
      surface: "#E6F3F7", // Azul muito claro (superfícies)
      secondary: "#6B7280", // Cinza base (stone)
      secondaryVariant: "#4B5563",
      secondaryLight: "#9CA3AF",
      secondarySurface: "#F8FAFC",
      charcoal: "#374151",
      silver: "#9CA3AF",
      mintWhisper: "#F0F9F4", // Mantido para compatibilidade (verde muito claro)
      // aliases para compatibilidade nominal antiga
      green: "#004E64",
      forest: "#003A4D",
      sage: "#006B85",
      stone: "#6B7280",
    },

    // Primary scale (azul) 50–900
    // 50 é bem claro (surface), 700 ~ main (#004E64), 800/900 mais escuros
    primary: {
      50: "#E6F3F7",
      100: "#CCE7EE",
      200: "#9FD2DE",
      300: "#72BCCF",
      400: "#45A5BF",
      500: "#006B85", // próximo do "primaryLight"
      600: "#005E74",
      700: "#004E64", // "primary"
      800: "#003A4D", // "primaryVariant"
      900: "#002A38",
    },

    // Secondary (escala cinza/slate)
    secondary: {
      50: "#F8FAFC",
      100: "#F1F5F9",
      200: "#E2E8F0",
      300: "#CBD5E1",
      400: "#94A3B8",
      500: "#64748B",
      600: "#475569",
      700: "#334155",
      800: "#1E293B",
      900: "#0F172A",
    },

    // Neutral (mantido alinhado ao slate)
    neutral: {
      50: "#F8FAFC",
      100: "#F1F5F9",
      200: "#E2E8F0",
      300: "#CBD5E1",
      400: "#94A3B8",
      500: "#64748B",
      600: "#475569",
      700: "#334155",
      800: "#1E293B",
      900: "#0F172A",
    },

    // Background
    background: {
      default: "#F9FAFB",
      paper: "#FFFFFF",
      elevated: "#F1F5F9",
      overlay: "rgba(0, 0, 0, 0.5)",
    },

    // Text
    text: {
      primary: "#0F172A",
      secondary: "#475569",
      tertiary: "#64748B",
      disabled: "#94A3B8",
      inverse: "#FFFFFF",
    },

    // Status (mantém os tons já aprovados)
    status: {
      success: {
        50: "#ECFDF5",
        100: "#D1FAE5",
        500: "#10B981",
        600: "#059669",
        700: "#047857",
      },
      warning: {
        50: "#FFFBEB",
        100: "#FEF3C7",
        500: "#F59E0B",
        600: "#D97706",
        700: "#B45309",
      },
      error: {
        50: "#FEF2F2",
        100: "#FEE2E2",
        500: "#EF4444",
        600: "#DC2626",
        700: "#B91C1C",
      },
      info: {
        50: "#EFF6FF",
        100: "#DBEAFE",
        500: "#3B82F6",
        600: "#2563EB",
        700: "#1D4ED8",
      },
    },

    // Organization Colors (Business Logic)
    organization: {
      farm: "#004E64",        // Azul profissional principal
      slaughterhouse: "#FF5722", // Mantido vermelho
      dairy: "#006B85",       // Azul claro
      cooperative: "#003A4D",  // Azul escuro
      other: "#6B7280",       // Cinza secundário
    },

    // Social – mantidos
    social: {
      whatsapp: "#25D366",
      instagram: "#E4405F",
      linkedin: "#0077B5",
    },
  },
} as const;

// Legacy color mappings for backward compatibility
// Agora referenciam a nova paleta azul
export const AGROTECH_COLORS = {
  // Primary Brand Palette (mantém os nomes para compatibilidade)
  AGRICULTURAL_GREEN: DESIGN_TOKENS.colors.brand.primary,
  DIGITAL_FOREST: DESIGN_TOKENS.colors.brand.primaryVariant,
  SMART_SAGE: DESIGN_TOKENS.colors.brand.primaryLight,
  PROFESSIONAL_STONE: DESIGN_TOKENS.colors.brand.secondary,
  CHARCOAL: DESIGN_TOKENS.colors.brand.charcoal,
  SILVER: DESIGN_TOKENS.colors.brand.silver,
  MINT_WHISPER: DESIGN_TOKENS.colors.brand.mintWhisper,
} as const;

// Legacy Colors (for backward compatibility)
export const LEGACY_COLORS = {
  // mapeia para a nova escala azul
  EDEN: DESIGN_TOKENS.colors.primary[600], // ~ #005E74
  GREEN_PEA: DESIGN_TOKENS.colors.primary[500], // ~ #006B85
  ZUCCINI: DESIGN_TOKENS.colors.primary[800], // ~ #003A4D

  APPLE: DESIGN_TOKENS.colors.secondary[500], // ~ #64748B
  NEBULA: DESIGN_TOKENS.colors.primary[100], // ~ #CCE7EE
  EDEN_ALPHA: DESIGN_TOKENS.colors.primary[700], // ~ #004E64
  OPAL: DESIGN_TOKENS.colors.primary[200], // ~ #9FD2DE

  GHOST_WHITE: DESIGN_TOKENS.colors.background.default,
  WHITE: DESIGN_TOKENS.colors.background.paper,
  MIRAGE: DESIGN_TOKENS.colors.text.primary,
  SHUTTLE_GRAY: DESIGN_TOKENS.colors.text.secondary,

  CATSKILL_WHITE: "#F1F5F9",
  CATSKILL_WHITE_BETA: "#E2E8F0",
  BRIGHT_GRAY: "#E5EBF0",

  SLATE_GRAY: DESIGN_TOKENS.colors.text.tertiary,
  OXFORD_BLUE: DESIGN_TOKENS.colors.brand.primary,

  SUCCESS: DESIGN_TOKENS.colors.status.success[500],
  ERROR: DESIGN_TOKENS.colors.status.error[500],
  WHATSAPP: DESIGN_TOKENS.colors.social.whatsapp,
} as const;

// Color utility functions
export const getOrganizationColor = (
  type: keyof typeof DESIGN_TOKENS.colors.organization
) => {
  return (
    DESIGN_TOKENS.colors.organization[type] ||
    DESIGN_TOKENS.colors.organization.other
  );
};

export const getStatusColor = (
  status: "success" | "warning" | "error" | "info",
  shade: 50 | 100 | 500 | 600 | 700 = 500
) => {
  return DESIGN_TOKENS.colors.status[status][shade];
};
